import React from 'react';
import ScaleAgencyLogo from '@/components/common/ScaleAgencyLogo';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * TenantLogo — Standardized Oblique System Design Primitive
 * 
 * DESIGN PRINCIPLE:
 * Straight, pure, unboxed brand presentation.
 * - NEVER wrapped inside capsules, pill borders, or background boxes.
 * - Respects the tenant's exact uploaded brand asset at natural aspect ratio.
 * - Automatically adapts to light & dark theme variants.
 */
export default function TenantLogo({
  tenant,
  company,
  height = 24,
  className = '',
  onClick,
  showDropdownChevron = false,
  title
}) {
  const { theme } = useTheme();

  const activeTenant = tenant || {};
  const activeCompany = company || {};

  const name = activeTenant.name || activeCompany.name || 'The Scale Agency';
  const isScaleAgency = (
    name.toLowerCase().includes('scale') ||
    activeTenant.id === 'tenant-scale-agency' ||
    activeCompany.id === 'c8aetB6uhQ3L4Eqwmw4I' ||
    activeTenant.domain?.includes('scale')
  );

  // Logo URL selection based on theme
  const customLogoUrl = theme === 'dark'
    ? (activeCompany.darkLogoUrl || activeTenant.darkLogoUrl || activeCompany.logoUrl || activeTenant.lightLogoUrl || null)
    : (activeCompany.lightLogoUrl || activeTenant.lightLogoUrl || activeCompany.logoUrl || activeTenant.darkLogoUrl || null);

  const renderLogoContent = () => {
    if (isScaleAgency) {
      return <ScaleAgencyLogo height={height} className="shrink-0" />;
    }

    if (customLogoUrl) {
      return (
        <img
          src={customLogoUrl}
          alt={name}
          style={{ height: `${height}px`, width: 'auto' }}
          className="object-contain max-h-full shrink-0"
        />
      );
    }

    // High-refinement fallback typography (Unboxed, pure text brand)
    return (
      <span 
        className="font-bold text-sm tracking-tight text-[var(--text-primary)] shrink-0 select-none"
        style={{ fontSize: `${Math.max(13, height * 0.6)}px` }}
      >
        {name}
      </span>
    );
  };

  const content = (
    <div className={`inline-flex items-center gap-1.5 select-none ${className}`}>
      {renderLogoContent()}
      {showDropdownChevron && (
        <ObliqueIcon 
          name="expand_more" 
          size={14} 
          className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors shrink-0" 
        />
      )}
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={title || `Switch Workspace (${name})`}
        className="inline-flex items-center p-0 m-0 border-0 bg-transparent cursor-pointer hover:opacity-80 transition-opacity focus:outline-none"
      >
        {content}
      </button>
    );
  }

  return content;
}
